import * as React from 'react';
import { cn } from '@restworld/utils-common';
// --> main component
export const Card = (props: CardTypes) => {
  const { paddingType, variant, children, className = 'item', isActive, twStyle, testId } = props;
  return (
    <StyledCard
      isActive={isActive}
      className={className}
      paddingType={paddingType}
      variant={variant}
      twStyle={twStyle}
      data-cy={testId}
    >
      {children}
    </StyledCard>
  );
};
export default Card;

// paddingType defaults to 'base' (p-4) to match master's styled-component default; the port
// dropped it, so cards that don't pass paddingType (e.g. JobPositionCard) rendered with no
// padding and their content sat flush to the card edges.
const StyledCard = ({ paddingType = 'base', variant, isActive, twStyle, noShadow, className, ...props }: any) => (
  <div
    className={cn(
      'min-h-[15rem] min-w-[9rem] w-full h-full',
      'flex self-center flex-col gap-6 border-gray-200 shadow relative rounded-2xl',
      paddingTypeMap[paddingType],
      variantBgMap[variant],
      isActive ? 'border-2 border-primary-800 shadow shadow-primary-800/30' : '',
      twStyle,
      noShadow ? 'shadow-none' : '',
      className
    )}
    {...props}
  />
);
// --> other card components
// Defaults must match master's styled-component defaults (fontSize='xLarge' -> text-3xl,
// textColor='white'); the port dropped them, so <CardTitle textColor={...}> with no fontSize
// fell back to inherited size (looked "just bold"). Same for CardSubTitle's purple200 default.
export const CardTitle = ({ fontSize = 'xLarge', textColor = 'white', className, ...props }: any) => (
  <div
    className={cn('font-semibold line-clamp-3', fontSizeMap[fontSize], textColorMap[textColor], className)}
    {...props}
  />
);
export const CardSubTitle = ({ textColor = 'purple200', className, ...props }: any) => (
  <span className={cn('font-normal text-sm w-full', textColorMap[textColor], className)} {...props} />
);
// --> constants
const fontSizeMap = {
  xLarge: 'text-3xl',
  large: 'text-2xl',
  medium: 'text-xl',
  base: 'text-base',
};
const textColorMap = {
  purple: 'text-primary-900',
  purple200: 'text-primary-200',
  white: 'text-white',
  black: 'text-black',
  gray: 'text-gray-600',
  gray300: 'text-gray-300',
};
const variantBgMap = {
  primary50: 'bg-primary-50',
  primary100: 'bg-primary-100',
  gradientBase: 'bg-gradient-base',
  white: 'bg-white',
  gray100: 'bg-gray-100',
  gray50: 'bg-gray-50 border border-gray-300',
};
const paddingTypeMap = {
  base: 'p-4',
  large: 'p-7',
};
// --> types
export interface CardTypes extends StyledCardType {
  children?: React.ReactNode;
  className?: string;
  testId?: string;
}
type StyledCardType = {
  paddingType?: paddingTypeMapType;
  variant?: variantBgMapType;
  isActive?: boolean;
  twStyle?: string;
  noShadow?: boolean;
};

export type variantBgMapType = keyof typeof variantBgMap;
export type paddingTypeMapType = keyof typeof paddingTypeMap;
